import React, { useState, useEffect } from "react";
import { Card, Button, Typography, Input, Badge, message, Modal, Spin, Form } from "antd";
import { SafetyOutlined, LockOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { setupMfa, verifyMfaSetup, disableMfa } from "../../Redux/APIs/mfa";
import { changePassword as apiChangePassword } from "../../Redux/APIs/changePassword";
import Layout from "../../ReusableComponents/Layout";
import LetterPrintSettings from "./LetterPrintSettings";

const { Title, Text, Paragraph } = Typography;

const Settings = () => {
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm] = Form.useForm();

  useEffect(() => {
    const adminData = localStorage.getItem("adminData");
    if (adminData) {
      try {
        const parsed = JSON.parse(adminData);
        setMfaEnabled(!!parsed.mfaEnabled);
      } catch (e) {}
    }
  }, []);

  const handleSetup = async () => {
    setLoading(true);
    try {
      const response = await setupMfa();
      setSetupData(response.data.data);
    } catch (err) {
      if (err.response?.data?.message === "MFA is already enabled") {
        setMfaEnabled(true);
      } else {
        message.error(err.response?.data?.message || "Failed to setup MFA");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySetup = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      message.error("Please enter a 6-digit code");
      return;
    }
    setLoading(true);
    try {
      await verifyMfaSetup(verifyCode);
      message.success("Two-factor authentication enabled");
      setMfaEnabled(true);
      setSetupData(null);
      setVerifyCode("");
      const adminData = localStorage.getItem("adminData");
      if (adminData) {
        const parsed = JSON.parse(adminData);
        parsed.mfaEnabled = true;
        localStorage.setItem("adminData", JSON.stringify(parsed));
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      const { currentPassword, newPassword } = await passwordForm.validateFields();
      setChangingPassword(true);
      await apiChangePassword(currentPassword, newPassword);
      message.success("Password changed");
      passwordForm.resetFields();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.response?.data?.message || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDisable = async () => {
    if (!disableCode || disableCode.length !== 6) {
      message.error("Please enter a 6-digit code");
      return;
    }
    setLoading(true);
    try {
      await disableMfa(disableCode);
      message.success("Two-factor authentication disabled");
      setMfaEnabled(false);
      setDisableModalOpen(false);
      setDisableCode("");
      const adminData = localStorage.getItem("adminData");
      if (adminData) {
        const parsed = JSON.parse(adminData);
        parsed.mfaEnabled = false;
        localStorage.setItem("adminData", JSON.stringify(parsed));
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
    <div style={{ padding: 24 }}>
      <Title level={2}>Settings</Title>

      <Card title="Two-Factor Authentication" style={{ maxWidth: 600 }}>
        {mfaEnabled && !setupData && (
          <div>
            <Badge
              status="success"
              text={<Text strong style={{ color: "#52c41a" }}>Enabled</Text>}
              style={{ marginBottom: 16, display: "block" }}
            />
            <Paragraph>
              Your account is protected with two-factor authentication.
              You will be asked for a verification code when signing in.
            </Paragraph>
            <Button danger onClick={() => setDisableModalOpen(true)}>
              Disable 2FA
            </Button>
          </div>
        )}

        {!mfaEnabled && !setupData && (
          <div>
            <Badge
              status="default"
              text={<Text type="secondary">Not enabled</Text>}
              style={{ marginBottom: 16, display: "block" }}
            />
            <Paragraph>
              Add an extra layer of security to your account. When enabled,
              you'll need to enter a code from your authenticator app
              (Google Authenticator, Authy, etc.) each time you sign in.
            </Paragraph>
            <Button type="primary" onClick={handleSetup} loading={loading}>
              Enable 2FA
            </Button>
          </div>
        )}

        {setupData && (
          <div>
            <Paragraph>
              Scan this QR code with your authenticator app, then enter
              the 6-digit code below to confirm setup.
            </Paragraph>
            <div style={{ textAlign: "center", margin: "24px 0" }}>
              <img
                src={setupData.qrCode}
                alt="MFA QR Code"
                style={{ width: 200, height: 200 }}
              />
            </div>
            <Paragraph type="secondary" style={{ fontSize: 12, textAlign: "center" }}>
              Manual entry key: {setupData.secret}
            </Paragraph>
            <Input
              prefix={<SafetyOutlined />}
              placeholder="000000"
              maxLength={6}
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ""))}
              onPressEnter={handleVerifySetup}
              size="large"
              style={{ marginBottom: 16, textAlign: "center", letterSpacing: 8, fontSize: 20 }}
            />
            <Button type="primary" block onClick={handleVerifySetup} loading={loading}>
              Verify & Enable
            </Button>
            <Button
              type="link"
              block
              onClick={() => { setSetupData(null); setVerifyCode(""); }}
              style={{ marginTop: 8 }}
            >
              Cancel
            </Button>
          </div>
        )}
      </Card>

      <Card title="Change Password" style={{ maxWidth: 600, marginTop: 24 }}>
        <Paragraph>
          Use this if you signed in with a temporary password or just want to
          rotate your password.
        </Paragraph>
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handleChangePassword}
        >
          <Form.Item
            name="currentPassword"
            label="Current Password"
            rules={[{ required: true, message: "Current password is required" }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              autoComplete="current-password"
            />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: "New password is required" },
              { min: 8, message: "Minimum 8 characters" },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              autoComplete="new-password"
            />
          </Form.Item>
          <Form.Item
            name="confirmNewPassword"
            label="Confirm New Password"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "Please confirm your new password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              autoComplete="new-password"
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={changingPassword}
            >
              Change Password
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <LetterPrintSettings />

      <Modal
        title="Disable Two-Factor Authentication"
        open={disableModalOpen}
        onCancel={() => { setDisableModalOpen(false); setDisableCode(""); }}
        onOk={handleDisable}
        okText="Disable 2FA"
        okButtonProps={{ danger: true, loading }}
      >
        <Paragraph>
          Enter your current authenticator code to disable two-factor authentication.
        </Paragraph>
        <Input
          prefix={<SafetyOutlined />}
          placeholder="000000"
          maxLength={6}
          value={disableCode}
          onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))}
          onPressEnter={handleDisable}
          size="large"
          style={{ textAlign: "center", letterSpacing: 8, fontSize: 20 }}
        />
      </Modal>
    </div>
    </Layout>
  );
};

export default Settings;
